import React from "react";
import {
  Box,
  Button,
  HStack,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useBreakpointValue,
  useColorModeValue,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import RiskReviewStatusBadge from "../common/RiskReviewStatusBadge";
import { formatDate, getHighestRisk, getRequestId, getRequesterName } from "../../utils/riskReviewFormatters";

const RiskReviewRequestTable = ({ requests = [] }) => {
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const headBg = useColorModeValue("gray.50", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");

  if (isMobile) {
    return (
      <VStack align="stretch" spacing={3}>
        {requests.map((request) => {
          const risk = getHighestRisk(request.risks);
          return (
            <Box key={request._id} bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="md" p={4} boxShadow="sm">
              <HStack justify="space-between" align="flex-start">
                <Box>
                  <Text fontWeight="semibold">{getRequestId(request)}</Text>
                  <Text color="gray.500">{request.projectName || request.businessUnit || "—"}</Text>
                </Box>
                <RiskReviewStatusBadge value={request.status} />
              </HStack>
              <HStack justify="space-between" mt={4}>
                <Text color="gray.500" fontSize="sm">
                  {request.requestType || "—"} • {formatDate(request.submittedAt || request.createdAt)}
                </Text>
                {risk ? <RiskReviewStatusBadge value={risk.severity} type="risk" /> : null}
                <Button as={RouterLink} to={`/risk-review/requests/${request._id}`} size="sm">
                  View
                </Button>
              </HStack>
            </Box>
          );
        })}
      </VStack>
    );
  }

  return (
    <TableContainer bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="md" boxShadow="sm">
      <Table size="sm">
        <Thead bg={headBg}>
          <Tr>
            <Th py={3}>Request ID</Th>
            <Th>Revision</Th>
            <Th>Type</Th>
            <Th>Project</Th>
            <Th>Business Unit</Th>
            <Th>Status</Th>
            <Th>Risk</Th>
            <Th>Submitted</Th>
            <Th>Requester</Th>
            <Th textAlign="right">Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {requests.map((request) => {
            const risk = getHighestRisk(request.risks);
            return (
              <Tr key={request._id} _hover={{ bg: hoverBg }}>
                <Td fontWeight="semibold" py={3}>
                  <Link as={RouterLink} to={`/risk-review/requests/${request._id}`} color="blue.600" _dark={{ color: "blue.300" }}>
                    {getRequestId(request)}
                  </Link>
                  <Text color="gray.500" fontSize="xs">
                    Revision {request.revisionNo || 0}
                  </Text>
                </Td>
                <Td>{request.revisionNo || 0}</Td>
                <Td>{request.requestType || "—"}</Td>
                <Td>
                  <Text>{request.projectName || "—"}</Text>
                  <Text color="gray.500" fontSize="xs">
                    {request.country || request.businessUnit || "—"}
                  </Text>
                </Td>
                <Td>{request.businessUnit || "—"}</Td>
                <Td>
                  <RiskReviewStatusBadge value={request.status} />
                </Td>
                <Td>{risk ? <RiskReviewStatusBadge value={risk.severity} type="risk" /> : "—"}</Td>
                <Td>{formatDate(request.submittedAt || request.createdAt)}</Td>
                <Td>{getRequesterName(request)}</Td>
                <Td textAlign="right">
                  <Button as={RouterLink} to={`/risk-review/requests/${request._id}`} size="sm" variant="outline" colorScheme="blue">
                    View
                  </Button>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default RiskReviewRequestTable;
